import { useEffect, useState } from "react";
import { Copy, Ban, Activity, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, dt } from "@/lib/api";
import { PageHeader, Badge2, Kpi } from "@/components/common";
import { copy, shareUrl } from "@/components/ShareDialog";

const status = (s) => (!s.is_active ? "Revoked" : s.expiry_date < new Date().toISOString() ? "Expired" : "Active");

export function SharesTable({ rows, onRevoke, onLogs, showOrg }) {
  return (
    <div className="rounded-xl border border-slate-800 overflow-x-auto bg-white">
      <table className="w-full text-sm" data-testid="shares-table">
        <thead className="bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400"><tr>{[showOrg && "Organization", "Report", "Type", "Created by", "Expires", "Views", "Last viewed", "Status", ""].filter((x) => x !== false).map((h) => <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{rows.map((s) => (
          <tr key={s.id} className="border-t border-slate-800" data-testid={`share-row-${s.id}`}>
            {showOrg && <td className="px-4 py-2.5">{s.organization_name}</td>}
            <td className="px-4 py-2.5 font-medium whitespace-nowrap">{s.password_protected && <Lock className="h-3.5 w-3.5 inline mr-1 text-cyan-400" />}{s.title}</td>
            <td className="px-4"><Badge2 tone="slate">{s.kind.replace(/_/g, " ")}</Badge2></td>
            <td className="px-4 whitespace-nowrap">{s.created_by_name}</td>
            <td className="px-4 text-xs font-mono whitespace-nowrap">{s.expiry_date.slice(0, 10)}</td>
            <td className="px-4 font-mono" data-testid={`share-views-${s.id}`}>{s.view_count}</td>
            <td className="px-4 text-xs font-mono whitespace-nowrap">{s.last_viewed_at ? dt(s.last_viewed_at) : "-"}</td>
            <td className="px-4"><Badge2 tone={status(s) === "Active" ? "green" : "rose"}>{status(s)}</Badge2></td>
            <td className="px-4 py-2 text-right whitespace-nowrap">
              <Button size="icon" variant="ghost" onClick={() => copy(shareUrl(s.share_token))} data-testid={`share-copy-${s.id}`}><Copy className="h-4 w-4" /></Button>
              {onLogs && <Button size="icon" variant="ghost" onClick={() => onLogs(s)} data-testid={`share-logs-${s.id}`}><Activity className="h-4 w-4" /></Button>}
              {onRevoke && s.is_active && <Button size="icon" variant="ghost" onClick={() => onRevoke(s)} data-testid={`share-revoke-${s.id}`}><Ban className="h-4 w-4 text-rose-400" /></Button>}
            </td>
          </tr>
        ))}</tbody>
      </table>
      {!rows.length && <div className="text-sm text-slate-500 p-6 text-center">No shared links yet.</div>}
    </div>
  );
}

export default function SharedLinks() {
  const [rows, setRows] = useState([]);
  const [logs, setLogs] = useState(null);
  const load = () => api.get("/shares").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const revoke = async (s) => {
    if (!window.confirm("Revoke this link? Viewers will lose access immediately.")) return;
    try { await api.post(`/shares/${s.id}/revoke`); toast.success("Link revoked"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const openLogs = async (s) => setLogs({ s, rows: (await api.get(`/shares/${s.id}/logs`)).data });
  const active = rows.filter((s) => status(s) === "Active");
  return (
    <div data-testid="shared-links-page">
      <PageHeader label="Organization" title="Smart Share Links" subtitle="Secure, expiring, view-only links to your reports and decks. Track views and revoke anytime." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Total links" value={rows.length} testid="shares-kpi-total" />
        <Kpi label="Active" value={active.length} testid="shares-kpi-active" />
        <Kpi label="Total views" value={rows.reduce((a, b) => a + b.view_count, 0)} accent="text-cyan-400" testid="shares-kpi-views" />
        <Kpi label="Password protected" value={rows.filter((s) => s.password_protected).length} accent="text-cyan-400" testid="shares-kpi-protected" />
      </div>
      <SharesTable rows={rows} onRevoke={revoke} onLogs={openLogs} />
      <Dialog open={!!logs} onOpenChange={() => setLogs(null)}>
        <DialogContent className="bg-white border-slate-800 max-w-2xl" data-testid="share-logs-dialog">
          <DialogHeader><DialogTitle>View analytics: {logs?.s.title}</DialogTitle><DialogDescription>{logs?.s.view_count} views</DialogDescription></DialogHeader>
          <div className="max-h-96 overflow-y-auto"><table className="w-full text-sm"><thead className="text-[11px] uppercase text-slate-500"><tr><th className="text-left py-2">Viewed at</th><th className="text-left">IP address</th><th className="text-left">Device</th></tr></thead>
            <tbody>{logs?.rows.map((l) => <tr key={l.id} className="border-t border-slate-800"><td className="py-2 text-xs font-mono">{dt(l.viewed_at)}</td><td className="font-mono text-xs">{l.ip_address}</td><td className="text-xs text-slate-500 truncate max-w-[220px]">{l.user_agent}</td></tr>)}</tbody></table>
            {!logs?.rows.length && <div className="text-sm text-slate-500 py-4">No views yet.</div>}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
